/**
 * Шапка карточки бренда: 44 px, sticky (§7).
 *
 * Состав по спеке: название, служебная строка (тир · адрес страницы · пилюля
 * каналов), четыре действия справа (ссылка, меню, отдельная страница,
 * закрыть), вкладки каналов 36 px с точкой «значения отличаются» и строка
 * «Пропущено для вас: N» — только при непустой зоне ответственности (§4.9).
 *
 * «⧉» копирует ссылку на бренд: в телефонном разговоре люди говорят
 * «посмотри Barazza», и это нужно каждый день (§7).
 */
import { useRef, useState } from 'react'
import type { ReactNode } from 'react'
import type { Brand, ChannelCode } from '@shared/types.ts'
import {
  Badge, Button, ConsequenceLine, IconArrowRight, IconClose, IconCopy, IconExternalLink,
  IconWarning, IconButton, Input, Menu, TierBadge, Tooltip, type MenuItem,
} from '../../../components/index.js'
import { brandCell, channelCell, type Overlay } from '../../../lib/draft-overlay.js'
import { useApp } from '../../../store/app-store.js'
import { prettyUrl } from './ChannelBlock.js'
import { cardTexts } from './texts.card.js'
import './BrandHeader.css'

export interface BrandHeaderProps {
  brand: Brand
  channel: ChannelCode
  onChannel: (channel: ChannelCode) => void
  /** Есть ли строгое расхождение значений между каналами (§7). */
  differs: boolean
  /** Пропуски зоны ответственности смотрящего по активному каналу. */
  missing: number
  onGoToMissing: () => void
  onClose: () => void
  /** Переход на отдельную страницу карточки (⤢). */
  pageHref: string
  onArchive: () => void
  onOpenHistory: () => void
  onFocusComment: () => void
  /**
   * Кнопка «Обсуждение» на телефоне. Стоит НАД вкладками каналов: разговор
   * один на весь бренд, своего чата у розницы и опта нет, и порядок на
   * экране должен это показывать.
   */
  talkSlot?: ReactNode
}

export function BrandHeader({
  brand, channel, onChannel, differs, missing, onGoToMissing, onClose,
  pageHref, onArchive, onOpenHistory, onFocusComment, talkSlot,
}: BrandHeaderProps) {
  const overlay = useApp((state) => state.overlay)
  const bootstrap = useApp((state) => state.bootstrap)
  const editBrand = useApp((state) => state.editBrand)
  const toast = useApp((state) => state.toast)

  const [menuOpen, setMenuOpen] = useState(false)
  const [renaming, setRenaming] = useState(false)
  const menuRef = useRef<HTMLButtonElement | null>(null)

  const channels = bootstrap?.channels ?? []
  const user = bootstrap?.user ?? null
  const canEdit = Boolean(user?.permissions.includes('brands.edit'))
  const canArchive = Boolean(user?.permissions.includes('brands.archive'))

  const nameCell = brandCell<string>(overlay, brand, 'name')
  const name = typeof nameCell.value === 'string' ? nameCell.value : brand.name
  const tierCell = channelCell<string>(overlay, brand, channel, 'tier')
  const urlCell = channelCell<string>(overlay, brand, channel, 'url')
  const url = typeof urlCell.value === 'string' ? urlCell.value : null

  const items: MenuItem[] = [
    { id: 'rename', label: cardTexts.menu.rename, disabled: !canEdit, reason: canEdit ? undefined : cardTexts.deny.viewer },
    { id: 'comment', label: cardTexts.menu.comment },
    { id: 'history', label: cardTexts.menu.history },
    {
      id: 'archive',
      label: cardTexts.menu.archive,
      danger: true,
      separatorBefore: true,
      disabled: !canArchive,
      reason: canArchive ? undefined : cardTexts.menu.archiveDenied,
    },
  ]

  async function copyLink(): Promise<void> {
    const link = `${window.location.origin}/brands/${brand.slug}`
    try {
      await navigator.clipboard.writeText(link)
      toast('success', cardTexts.head.linkCopied)
    } catch {
      toast('error', cardTexts.head.linkCopyFailed)
    }
  }

  return (
    <header className="card-head">
      <div className="card-head__top">
        {renaming ? (
          <RenameField
            value={name}
            onCancel={() => setRenaming(false)}
            onSubmit={(next) => {
              setRenaming(false)
              if (next !== name) void editBrand(brand.id, { name: next })
            }}
          />
        ) : (
          <h2 className={nameCell.isDraft ? 'card-head__name t-h1 is-draft' : 'card-head__name t-h1'}>
            {name}
          </h2>
        )}

        {overlay.draftBrands.has(brand.id) ? (
          <Tooltip content={cardTexts.head.draftBrandHint}>
            <span className="card-head__flag">
              <Badge variant="draft">{cardTexts.head.draftBrand}</Badge>
            </span>
          </Tooltip>
        ) : null}
        {brand.status === 'archived' ? (
          <Badge variant="neutral">{cardTexts.head.archived}</Badge>
        ) : null}

        <div className="card-head__actions">
          <IconButton
            label={cardTexts.head.copyLink}
            icon={<IconCopy size={16} />}
            variant="ghost"
            onClick={() => void copyLink()}
          />
          <IconButton
            ref={menuRef}
            label={cardTexts.head.actions}
            icon={<IconDots />}
            variant="ghost"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((previous) => !previous)}
          />
          <a className="card-head__page" href={pageHref} aria-label={cardTexts.head.openPage}>
            <IconExternalLink size={16} />
          </a>
          <IconButton
            label={cardTexts.head.close}
            icon={<IconClose size={16} />}
            variant="ghost"
            onClick={onClose}
          />
        </div>
      </div>

      {/* Строка меты рисуется только когда есть что показать: одинокий прочерк
          под названием выглядел как опечатка. */}
      {(typeof tierCell.value === 'string' || url) ? (
      <p className="card-head__meta t-meta">
        {typeof tierCell.value === 'string' ? <TierBadge tier={tierCell.value} /> : null}
        {url ? (
          <>
            <span aria-hidden="true"> · </span>
            <a className="card-head__url" href={url} target="_blank" rel="noreferrer noopener">
              {prettyUrl(url)}
              <IconExternalLink size={16} />
            </a>
          </>
        ) : null}
      </p>
      ) : null}

      {talkSlot}

      <div className="card-head__tabs" role="tablist" aria-label={cardTexts.channels.tabsLabel}>
        {channels.map((item) => {
          const selected = item.code === channel
          return (
            <button
              key={item.code}
              type="button"
              role="tab"
              id={`card-channel-${item.code}`}
              aria-selected={selected}
              aria-controls="card-channel-panel"
              tabIndex={selected ? 0 : -1}
              className={selected ? 'card-head__tab card-head__tab--on' : 'card-head__tab'}
              onClick={() => onChannel(item.code)}
              onKeyDown={(event) => {
                if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
                event.preventDefault()
                const other = channels.find((candidate) => candidate.code !== item.code)
                if (other) onChannel(other.code)
              }}
            >
              <span className="card-head__tab-name t-btn">{item.name}</span>
              {!selected && differs ? (
                <span className="card-head__tab-dot" aria-label={cardTexts.channels.differs} />
              ) : null}
            </button>
          )
        })}
      </div>

      {missing > 0 ? (
        <button type="button" className="card-head__missing t-meta" onClick={onGoToMissing}>
          <IconWarning size={16} />
          <span>{cardTexts.missing.label(missing)}</span>
          <IconArrowRight size={16} />
        </button>
      ) : null}

      <Menu
        open={menuOpen}
        anchor={menuRef.current}
        items={items}
        label={cardTexts.head.actions}
        placement="bottom-end"
        onClose={() => setMenuOpen(false)}
        onSelect={(id) => {
          setMenuOpen(false)
          if (id === 'rename') setRenaming(true)
          if (id === 'archive') onArchive()
          if (id === 'history') onOpenHistory()
          if (id === 'comment') onFocusComment()
        }}
      />
    </header>
  )
}

/** Пилюля «Р·О»: буква активного канала залита, второй канал — обводка или зачёркнутая (§4.6). */
interface ChannelPillProps {
  brand: Brand
  overlay: Overlay
  channels: { code: ChannelCode; name: string; shortName: string }[]
  active: ChannelCode
}

function ChannelPill({ brand, overlay, channels, active }: ChannelPillProps) {
  return (
    <span className="card-pill">
      {channels.map((item) => {
        const cell = channelCell<boolean>(overlay, brand, item.code, 'isPresent')
        const present = cell.value === true
        const classes = ['card-pill__letter']
        if (item.code === active) classes.push('card-pill__letter--active')
        if (!present) classes.push('card-pill__letter--absent')
        if (cell.isDraft) classes.push('card-pill__letter--draft')
        return (
          <span
            key={item.code}
            className={classes.join(' ')}
            aria-label={present ? cardTexts.head.presentIn(item.name) : cardTexts.head.absentIn(item.name)}
          >
            {item.name.slice(0, 1)}
          </span>
        )
      })}
    </span>
  )
}

interface RenameFieldProps {
  value: string
  onCancel: () => void
  onSubmit: (value: string) => void
}

function RenameField({ value, onCancel, onSubmit }: RenameFieldProps) {
  const [draft, setDraft] = useState(value)
  const invalid = draft.trim() === ''

  return (
    <div className="card-head__rename">
      <Input
        value={draft}
        autoFocus
        maxLength={120}
        aria-label={cardTexts.rename.label}
        error={invalid ? cardTexts.rename.required : undefined}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !invalid) {
            event.preventDefault()
            onSubmit(draft.trim())
          }
          if (event.key === 'Escape') {
            event.preventDefault()
            onCancel()
          }
        }}
      />
      <ConsequenceLine>{cardTexts.rename.consequence}</ConsequenceLine>
      <div className="card-head__rename-actions">
        <Button variant="secondary" size="m" disabled={invalid} onClick={() => onSubmit(draft.trim())}>
          {cardTexts.rename.save}
        </Button>
        <Button variant="ghost" size="m" onClick={onCancel}>
          {cardTexts.rename.cancel}
        </Button>
      </div>
    </div>
  )
}

/** Иконка «⋯»: в наборе её нет, а меню шапки по §7 обязано быть. */
function IconDots() {
  return (
    <svg className="icon" width="16" height="16" viewBox="0 0 20 20" fill="none"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
      <circle cx="4.5" cy="10" r=".9" fill="currentColor" />
      <circle cx="10" cy="10" r=".9" fill="currentColor" />
      <circle cx="15.5" cy="10" r=".9" fill="currentColor" />
    </svg>
  )
}
